import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaBell, FaLink, FaBellSlash, FaCheckCircle } from 'react-icons/fa';
import { remindersApi } from '../api/remindersApi';
import { useLanguage } from '../contexts/LanguageContext';
import notificationService from '../services/NotificationService';
import styles from '../styles/reminders/ReminderCreateDialog.module.css';

const FREQUENCY_OPTIONS = [
  { value: 'once', labelKey: 'reminderOnce', fallback: 'Einmalig' },
  { value: 'daily', labelKey: 'reminderDaily', fallback: 'Täglich' },
  { value: 'weekly', labelKey: 'reminderWeekly', fallback: 'Wöchentlich' },
  { value: 'monthly', labelKey: 'reminderMonthly', fallback: 'Monatlich' },
];

const QUICK_OPTIONS = [
  { minutes: 30, labelKey: 'in30Min', fallback: 'In 30 Min' },
  { minutes: 60, labelKey: 'in1Hour', fallback: 'In 1 Stunde' },
  { minutes: 180, labelKey: 'in3Hours', fallback: 'In 3 Stunden' },
  { minutes: 1440, labelKey: 'tomorrow', fallback: 'Morgen' },
  { minutes: 10080, labelKey: 'nextWeek', fallback: 'Nächste Woche' },
];

export default function ReminderCreateDialog({ bookmark, onClose, onCreated }) {
  const { t } = useLanguage();
  const [frequency, setFrequency] = useState('once');
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    notificationService.getPermission()
  );

  // Request notification permission on mount
  useEffect(() => {
    const requestPermission = async () => {
      if (notificationService.isSupported() && notificationPermission === 'default') {
        const permission = await notificationService.requestPermission();
        setNotificationPermission(permission);
      }
    };
    requestPermission();
  }, [notificationPermission]);

  const handleQuickOption = async (minutes) => {
    setLoading(true);
    setError(null);
    try {
      const nextAt = new Date(Date.now() + minutes * 60000).toISOString();
      await remindersApi.create({
        title: bookmark.title || 'Lesezeichen ansehen',
        icon: '🔖',
        frequency,
        nextAt,
        bookmarkId: bookmark.id,
        bookmarkUrl: bookmark.url,
        bookmarkTitle: bookmark.title,
      });
      setSuccess(true);
      onCreated && onCreated();
      // Dialog nach 1.5 Sekunden automatisch schließen
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (e) {
      setError(e.message || 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    if (!customDate || !customTime) {
      setError(t('pleaseSelectDateTime') || 'Bitte Datum und Uhrzeit auswählen');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const nextAt = new Date(`${customDate}T${customTime}`).toISOString();
      await remindersApi.create({
        title: bookmark.title || 'Lesezeichen ansehen',
        icon: '🔖',
        frequency,
        nextAt,
        bookmarkId: bookmark.id,
        bookmarkUrl: bookmark.url,
        bookmarkTitle: bookmark.title,
      });
      setSuccess(true);
      onCreated && onCreated();
      // Dialog nach 1.5 Sekunden automatisch schließen
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (e) {
      setError(e.message || 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  const formatDomain = (url) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <FaBell className={styles.headerIcon} />
            <h2 className={styles.headerTitle}>
              {t('createReminder') || 'Erinnerung erstellen'}
            </h2>
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('close') || 'Schließen'}
            disabled={loading}
          >
            <FaTimes />
          </button>
        </header>

        {/* Bookmark Info */}
        <div className={styles.bookmarkInfo}>
          <FaLink className={styles.linkIcon} />
          <div className={styles.bookmarkDetails}>
            <span className={styles.bookmarkTitle}>
              {(bookmark.title || 'Untitled').length > 40
                ? (bookmark.title || 'Untitled').substring(0, 40) + '...'
                : (bookmark.title || 'Untitled')}
            </span>
            <span className={styles.bookmarkUrl}>
              {formatDomain(bookmark.url)}
            </span>
          </div>
        </div>

        {/* Notification Permission Warning */}
        {notificationPermission === 'denied' && (
          <div className={styles.warningBanner} role="alert">
            <FaBellSlash />
            <span>{t('notificationsDenied') || 'Push-Benachrichtigungen wurden blockiert. Bitte aktiviere sie in den Browser-Einstellungen.'}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className={styles.successBanner} role="status">
            <FaCheckCircle />
            <span>{t('reminderCreated') || 'Erinnerung erstellt!'}</span>
          </div>
        )}

        {/* Frequency Selection */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>
            {t('frequency') || 'Wiederholung'}
          </label>
          <div className={styles.frequencyGrid}>
            {FREQUENCY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.frequencyBtn} ${frequency === opt.value ? styles.active : ''}`}
                onClick={() => setFrequency(opt.value)}
                disabled={loading}
              >
                {t(opt.labelKey) || opt.fallback}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Options */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>
            {t('quickReminder') || 'Schnell erinnern'}
          </label>
          <div className={styles.quickGrid}>
            {QUICK_OPTIONS.map((opt) => (
              <button
                key={opt.minutes}
                type="button"
                className={styles.quickBtn}
                onClick={() => handleQuickOption(opt.minutes)}
                disabled={loading}
              >
                {t(opt.labelKey) || opt.fallback}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Date/Time */}
        <form className={styles.section} onSubmit={handleCustomSubmit}>
          <label className={styles.sectionLabel}>
            {t('customDateTime') || 'Eigenes Datum & Uhrzeit'}
          </label>
          <div className={styles.customRow}>
            <input
              type="date"
              className={styles.dateInput}
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              disabled={loading}
            />
            <input
              type="time"
              className={styles.timeInput}
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              disabled={loading}
            />
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={loading || !customDate || !customTime}
            >
              {loading ? '...' : (t('create') || 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
